import React, { useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Popconfirm, Row, Switch, Table, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import { LostReasonDto } from '../../dto/lost-reason-models';
import { permanentDeleteLostReason, saveLostReason, setLostReasonActive } from '../../services/lost-reason-service';

const { Title, Text } = Typography;

export type LostReasonsTabProps = {
  reasons: LostReasonDto[];
  onRefresh: () => Promise<void>;
};

export const LostReasonsTab: React.FC<LostReasonsTabProps> = ({ reasons, onRefresh }) => {
  const { instance, inProgress } = useMsal();
  const { notification } = App.useApp();
  const [editing, setEditing] = useState<LostReasonDto | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form] = Form.useForm();
  const [tablePage, setTablePage] = useState({ current: 1, pageSize: 20 });
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const sortedReasons = useMemo(
    () =>
      [...reasons].sort((a, b) =>
        a.englishLabel.localeCompare(b.englishLabel, undefined, { sensitivity: 'base' }),
      ),
    [reasons],
  );

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setExpandedRowKeys([]);
  };

  const openNew = () => {
    setExpandedRowKeys([]);
    setEditing({
      id: '',
      code: null,
      englishLabel: '',
      germanLabel: '',
      isActive: true,
    });
    form.setFieldsValue({
      code: '',
      englishLabel: '',
      germanLabel: '',
      isActive: true,
    });
    setShowForm(true);
  };

  const openEdit = (r: LostReasonDto) => {
    if (!r.id) return;
    const idx = sortedReasons.findIndex((x) => x.id === r.id);
    if (idx >= 0) {
      const page = Math.floor(idx / tablePage.pageSize) + 1;
      setTablePage((p) => ({ ...p, current: page }));
    }
    setExpandedRowKeys([r.id]);
    setEditing({ ...r });
    form.setFieldsValue({
      code: r.code ?? '',
      englishLabel: r.englishLabel,
      germanLabel: r.germanLabel,
      isActive: r.isActive,
    });
    setShowForm(true);
  };

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
      const res = await saveLostReason(token.accessToken, {
        id: editing?.id || null,
        code: values.code?.trim() || null,
        englishLabel: values.englishLabel,
        germanLabel: values.germanLabel,
        isActive: values.isActive,
      });
      if (res.isSuccess) {
        notification.success({ message: 'BOS lost reason saved' });
        closeForm();
        await onRefresh();
      } else {
        notification.error({ message: res.errors?.join(', ') ?? 'Save failed' });
      }
    } catch {
    }
  };

  const onDeactivate = async (r: LostReasonDto) => {
    if (!r.id) {
      notification.error({ message: 'Cannot deactivate an unsaved BOS lost reason.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await setLostReasonActive(token.accessToken, r.id, false);
    if (res.isSuccess) {
      notification.success({ message: 'BOS lost reason deactivated' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Deactivate failed' });
    }
  };

  const onPermanentDelete = async (r: LostReasonDto) => {
    if (!r.id) {
      notification.error({ message: 'Cannot delete an unsaved BOS lost reason.' });
      return;
    }
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const res = await permanentDeleteLostReason(token.accessToken, r.id);
    if (res.isSuccess) {
      notification.success({ message: 'BOS lost reason deleted permanently' });
      await onRefresh();
    } else {
      notification.error({ message: (res as any).errors?.join?.(', ') ?? 'Delete failed' });
    }
  };

  const reasonFormItems = (
    <>
      <Form.Item
        name="code"
        label="Code (optional)"
        extra={editing?.code ? 'A saved code cannot be changed.' : 'Stable identifier for Lead Handling; existing reasons may have no code.'}
        rules={[{ pattern: /^(?:[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*)?$/, message: 'Use letters, digits and single underscores between words' }]}
      >
        <Input placeholder="e.g. 1101_BUILDABILITY_PV_HP_NOT_AN_OWNER" maxLength={200} disabled={!!editing?.code} />
      </Form.Item>
      <Form.Item name="englishLabel" label="English (BOS / booking UI)" rules={[{ required: true }]}>
        <Input placeholder="Shown to bookers (English)" />
      </Form.Item>
      <Form.Item name="germanLabel" label="German (BOS / booking UI)" rules={[{ required: true }]}>
        <Input placeholder="Shown to bookers (German)" />
      </Form.Item>
      <Form.Item name="isActive" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Button type="primary" onClick={() => void onSubmit()}>
        Save
      </Button>
      <Button style={{ marginLeft: 8 }} onClick={() => closeForm()}>
        Cancel
      </Button>
    </>
  );

  const columns = [
    {
      title: '#',
      key: 'seq',
      width: 52,
      render: (_: unknown, __: LostReasonDto, index: number) =>
        (tablePage.current - 1) * tablePage.pageSize + index + 1,
    },
    { title: 'EN (BOS)', dataIndex: 'englishLabel', key: 'en' },
    { title: 'DE (BOS)', dataIndex: 'germanLabel', key: 'de' },
    { title: 'Code', dataIndex: 'code', key: 'code', width: 300, ellipsis: { showTitle: true }, render: (code: string | null) => code || '—' },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'active',
      width: 80,
      render: (v: boolean) => (
        <Text
          type={v ? 'success' : undefined}
          strong
          style={v ? undefined : { color: 'var(--ant-color-text-disabled)' }}
        >
          {v ? 'Yes' : 'No'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'a',
      width: 320,
      render: (_: unknown, r: LostReasonDto) => (
        <>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
            Edit
          </Button>
          {r.isActive && (
            <Button size="small" style={{ marginLeft: 8 }} onClick={() => void onDeactivate(r)}>
              Deactivate
            </Button>
          )}
          <Popconfirm
            title="Delete permanently?"
            description="Removes this BOS reason from all groups and deletes the row and its source-system mappings."
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
            onConfirm={() => void onPermanentDelete(r)}
          >
            <Button size="small" danger style={{ marginLeft: 8 }}>
              Delete
            </Button>
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div
      className="container flex-admin-vertical-container"
      style={{ width: '100%', maxWidth: 1200, minWidth: 0, justifyContent: 'flex-start' }}
    >
      <Row style={{ marginBottom: 8, flexDirection: 'column' }}>
        <Title level={4} style={{ marginBottom: 0 }}>
          BOS lost reasons
        </Title>
        <Text type="secondary">
          Canonical labels shown in booking. Assign reasons to a channel group to make them available in that channel.
          Existing Salesforce flows also need a source-system mapping.
        </Text>
      </Row>

      {showForm && editing && !editing.id ? (
        <Card style={{ marginBottom: 24 }}>
          <Title level={5}>New BOS lost reason</Title>
          <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
            {reasonFormItems}
          </Form>
        </Card>
      ) : null}

      <Row justify="end" style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={openNew}
          disabled={showForm && !editing?.id}
        >
          Add BOS reason
        </Button>
      </Row>

      <Table
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={sortedReasons}
        pagination={{
          current: tablePage.current,
          pageSize: tablePage.pageSize,
          showSizeChanger: true,
          onChange: (page, pageSize) => {
            setTablePage({ current: page, pageSize: pageSize ?? 20 });
            setExpandedRowKeys([]);
            if (editing?.id) {
              setShowForm(false);
              setEditing(null);
            }
          },
        }}
        expandable={{
          showExpandColumn: false,
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
          expandedRowRender: (record) => {
            if (!editing?.id || record.id !== editing.id) return null;
            return (
              <Card size="small" style={{ marginBottom: 8 }} styles={{ body: { paddingBottom: 12 } }}>
                <Title level={5} style={{ marginTop: 0 }}>
                  Edit BOS lost reason
                </Title>
                <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
                  {reasonFormItems}
                </Form>
              </Card>
            );
          },
        }}
      />
    </div>
  );
};
